import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from '../../../tooltip/Tooltip';
import { CoinsLabel } from '../coinsLabel/CoinsLabel';
import { ManualDataRegistration } from '../../../manual-data-registration/ManualDataRegistration';
import type { ManualDataRegistrationProps } from '../../../manual-data-registration/ManualDataRegistration';
import { LinkButton } from '../../../button/LinkButton';
import {
	PersonalDataRoot,
	PersonalDataHeader,
	PersonalDataHeaderLeft,
	PersonalDataUserInfo,
	PersonalDataUserName,
	PersonalDataUserNameWrapper,
	PersonalDataListHeader,
	PersonalDataListHeaderTitle,
	PersonalDataList,
	PersonalDataEmptyState,
	PersonalDataEmptyStateText,
} from './PersonalData.styled';

export interface PersonalDataRegistration {
	id: string;
	registration: ManualDataRegistrationProps;
}

export interface PersonalDataProps {
	userName: string;
	coins: number;
	registrations?: PersonalDataRegistration[];
	emptyLabel?: string;
	registrationsLabel?: string;
	addLabel?: string;
	storeLabel?: string;
	showRewardStore?: boolean;
}

const PersonalData = React.forwardRef<HTMLDivElement, PersonalDataProps>(
	function PersonalData({
		userName,
		coins,
		registrations = [],
		emptyLabel = 'There is no registration option yet',
		registrationsLabel = 'My Registrations:',
		addLabel = 'Add',
		storeLabel = 'Open Store',
		showRewardStore = true,
	}, ref) {
		const userNameRef = useRef<HTMLSpanElement>(null);
		const [isOverflowing, setIsOverflowing] = useState(false);

		useEffect(() => {
			const el = userNameRef.current;
			if (!el) return;
			const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
			check();
			const observer = new ResizeObserver(check);
			observer.observe(el);
			return () => observer.disconnect();
		}, [userName]);

		return (
			<PersonalDataRoot ref={ref}>
				<PersonalDataHeader>
					<PersonalDataHeaderLeft>
						<PersonalDataUserInfo>
							<Tooltip message={isOverflowing ? userName : undefined} placement="top">
								<PersonalDataUserNameWrapper>
									<PersonalDataUserName ref={userNameRef}>{userName}</PersonalDataUserName>
								</PersonalDataUserNameWrapper>
							</Tooltip>
							{showRewardStore && (
								<LinkButton variant="link" label={storeLabel} href="/rewards/store/" />
							)}
						</PersonalDataUserInfo>
					</PersonalDataHeaderLeft>
					{showRewardStore && <CoinsLabel amount={coins} valueLabel="coin" />}
				</PersonalDataHeader>

				{registrations.length === 0 ? (
					<PersonalDataEmptyState>
						<PersonalDataEmptyStateText>{emptyLabel}</PersonalDataEmptyStateText>
						<LinkButton
							variant="secondary"
							icon="plus"
							iconPosition="left"
							label={addLabel}
							href="/admin/data-sources/"
							size="small"
						/>
					</PersonalDataEmptyState>
				) : (
					<>
						<PersonalDataListHeader>
							<PersonalDataListHeaderTitle>{registrationsLabel}</PersonalDataListHeaderTitle>
							<LinkButton variant="link" label={addLabel} href="/admin/data-sources/" />
						</PersonalDataListHeader>
						<PersonalDataList>
							{registrations.map((reg) => (
								<ManualDataRegistration key={reg.id} {...reg.registration} />
							))}
						</PersonalDataList>
					</>
				)}
			</PersonalDataRoot>
		);
	}
);

export { PersonalData };
